import { useState } from "react";
import type { NetworkDestination } from "../../../../db/schema";
import { destinationSchema } from "../../../../shared/mutators/egressRouteShared";

export const invalidCidrHint =
  "Enter a valid CIDR range with a prefix length, e.g. 10.0.0.0/16.";

function parseDestinationDraft(
  type: NetworkDestination["type"],
  raw: string
): { destination: NetworkDestination | null; hint: string | null } {
  const trimmed = raw.trim();
  if (!trimmed) return { destination: null, hint: null };
  const destination: NetworkDestination =
    type === "cidr"
      ? { type: "cidr", range: trimmed }
      : { type: "hostnameGlob", pattern: trimmed };
  if (!destinationSchema.safeParse(destination).success) {
    // Only the CIDR arm can fail: hostnameGlob accepts any non-empty string.
    return { destination: null, hint: invalidCidrHint };
  }
  return { destination, hint: null };
}

export type DestinationDraft = ReturnType<typeof useDestinationDraft>;

// The inline hint stays hidden while the user is still typing: it appears only
// once the draft settles (the input blurs, or an add is attempted), and hides
// again as soon as the draft changes. `disabledReason` carries the same message
// unsettled, for a tooltip on the disabled add/save button — hovering it is an
// explicit ask for the reason, so it never counts as nagging.
export function useDestinationDraft(
  type: NetworkDestination["type"],
  initial = ""
) {
  const [draft, setDraftState] = useState(initial);
  const [isSettled, setIsSettled] = useState(false);
  const { destination, hint } = parseDestinationDraft(type, draft);
  const setDraft = (value: string) => {
    setDraftState(value);
    setIsSettled(false);
  };
  const attempt = () => {
    if (!destination) setIsSettled(true);
    return destination;
  };
  return {
    draft,
    setDraft,
    settle: () => setIsSettled(true),
    attempt,
    destination,
    disabledReason: hint,
    hint: isSettled ? hint : null,
  };
}
